import { LineImageAlt } from '@qr-front/icons/line-image-alt'
import { LineImagePlus } from '@qr-front/icons/line-image-plus'
import { isDesktop } from '@qr-front/ui/utils'
import { useAtom, useSetAtom } from 'jotai'
import { atomWithReset, RESET } from 'jotai/utils'
import { useTranslations } from 'next-intl'
import { forwardRef, useImperativeHandle } from 'react'

import { TUploadedMedia } from '@/services'
import { getStore } from '@/store'
import { TTranslationFunction } from '@/types'

import { FileUploadToMediaStorage } from '../../../components/FileUploadToMediaStorage'
import { isPreviewDialogOpenAtom, openOverlayIdAtom } from '../generatorState'
import { TContentFormHandle, TQrContentFormSection } from './qrContentFormTypes'

export const WELCOME_SCREEN_FORM_ID = 'welcome-screen'

// 5MB
const MAX_FILE_SIZE = 5 * 1024 * 1024

export const welcomeImageAtom = atomWithReset<TUploadedMedia | null>(null)
export const welcomeImageFileAtom = atomWithReset<File | null>(null)

export const resetWelcomeScreenForm = () => {
	getStore().set(welcomeImageAtom, RESET)
	getStore().set(welcomeImageFileAtom, RESET)
}

export const WELCOME_IMAGE_OVERLAY_ID = 'welcome-image'

export const WelcomeScreenForm = forwardRef<TContentFormHandle>((_, ref) => {
	const t = useTranslations()

	useImperativeHandle(
		ref,
		() => ({
			validate: () => Promise.resolve({ isValid: true }),
		}),
		[],
	)

	const setWelcomeImage = useSetAtom(welcomeImageAtom)
	const [file, setFile] = useAtom(welcomeImageFileAtom)
	const setIsPreviewDialogOpen = useSetAtom(isPreviewDialogOpenAtom)
	const setOpenOverlayId = useSetAtom(openOverlayIdAtom)

	const fileUploadTranslations = {
		title: t(
			'generator__content_form_section__welcome_screen__upload_title',
		),
		caption: t(
			'generator__content_form_section__welcome_screen__upload_caption',
			{ size: MAX_FILE_SIZE / 1024 / 1024 },
		),
		deleteButtonLabel: t('ui__file_upload__delete_button'),
		unsupportedFormatErrorMessage: t(
			'ui__file_upload__unsupported_format_error',
		),
		sizeTooLargeErrorMessage: t('ui__file_upload__file_size_error', {
			maxSize: MAX_FILE_SIZE / 1024 / 1024,
		}),
	}

	const cropDialogTranslations = {
		zoom: t('ui__image_crop_dialog__zoom'),
		closeButton: t('ui__popup__close_button'),
		saveButton: t('ui__image_crop_dialog__save_button'),
	}

	return (
		<>
			<label className="type-body-medium font-semibold">
				{t('generator__content_form_section__welcome_screen__image')}
			</label>
			<FileUploadToMediaStorage
				uploadPurpose="content_image"
				initialFileList={file ? [file] : []}
				onUploadStart={(incoming) => {
					setFile(incoming)
				}}
				onUploadComplete={(uploaded, originalFile) => {
					setFile(originalFile)
					setWelcomeImage(uploaded)
				}}
				onDelete={() => {
					setWelcomeImage(null)
					setFile(null)
				}}
				resizeResolution={{ width: 2048, height: 2048 }}
				showCropDialog
				maxFileSize={MAX_FILE_SIZE}
				placeholderIcon={<LineImageAlt />}
				showPreviewButton
				onClickShowPreview={() => {
					if (isDesktop()) {
						setOpenOverlayId(WELCOME_IMAGE_OVERLAY_ID)
					} else {
						setIsPreviewDialogOpen(true)
					}
				}}
				translations={fileUploadTranslations}
				cropDialogTranslations={cropDialogTranslations}
				className="mt-2"
			/>
		</>
	)
})

WelcomeScreenForm.displayName = 'WelcomeScreenForm'

export const welcomeScreenFormConfig = (
	t: TTranslationFunction,
): TQrContentFormSection => ({
	id: WELCOME_SCREEN_FORM_ID,
	title: t('generator__content_form_section__welcome_screen__title'),
	description: t(
		'generator__content_form_section__welcome_screen__description',
	),
	icon: <LineImagePlus />,
	Content: WelcomeScreenForm,
})
